import {DetailsCard, H3, Loading} from '@hm/fundamental'
import {EChart} from '@hm/fundamental-echarts'
import React, {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useMonthlyChartOption, useYearlyChartOption} from '../../hooks'
import {
  ChartViewMode,
  DifficultyFilter,
  MonthlyHierarchyAnalysisDataPoint,
  ValueDisplayMode,
  YearlyHierarchyAnalysisDataPoint
} from '../../types'

import {ChartToolbar} from './ChartToolbar'

type DataPointsChartProps = {
  monthlyData?: MonthlyHierarchyAnalysisDataPoint[]
  yearlyData?: YearlyHierarchyAnalysisDataPoint[]
  year: number
  isLoading: boolean
  getNodeName: (nodeId: string) => string
  viewMode: ChartViewMode
  onViewModeChange: (mode: ChartViewMode) => void
  years: number[]
  difficultyFilter: DifficultyFilter
  onDifficultyFilterChange: (filter: DifficultyFilter) => void
  categoryFilter: string[]
  onCategoryFilterChange: (sectionIds: string[]) => void
  valueDisplayMode: ValueDisplayMode
  onValueDisplayModeChange: (mode: ValueDisplayMode) => void
  visibleSeriesIds: string[] | null
  onVisibleSeriesIdsChange: (ids: string[] | null) => void
  selectedYear: number
  onSelectedYearChange: (year: number) => void
  availableYears: number[]
}

export const DataPointsChart: FC<DataPointsChartProps> = ({
  monthlyData = [],
  yearlyData = [],
  year,
  isLoading,
  getNodeName,
  viewMode,
  onViewModeChange,
  years,
  difficultyFilter,
  onDifficultyFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  valueDisplayMode,
  onValueDisplayModeChange,
  visibleSeriesIds,
  onVisibleSeriesIdsChange,
  selectedYear,
  onSelectedYearChange,
  availableYears
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {hierarchyEntries} = useMemo(() => {
    const ids =
      viewMode === 'monthly'
        ? monthlyData.map((item) => item.hierarchyId)
        : yearlyData.map((item) => item.hierarchyId)
    const hierarchyIds = [...new Set(ids)]
    return {
      hierarchyEntries: hierarchyIds
        .map((id) => ({id, name: getNodeName(id)}))
        .sort((a, b) => a.name.localeCompare(b.name))
    }
  }, [monthlyData, yearlyData, getNodeName, viewMode])

  const effectiveVisibleIds = useMemo(
    () => (!visibleSeriesIds ? hierarchyEntries.map((e) => e.id) : visibleSeriesIds),
    [visibleSeriesIds, hierarchyEntries]
  )

  const monthlyOption = useMonthlyChartOption({
    monthlyData,
    year,
    hierarchyEntries,
    effectiveVisibleIds,
    difficultyFilter,
    valueDisplayMode,
    enabled: viewMode === 'monthly'
  })

  const yearlyOption = useYearlyChartOption({
    yearlyData,
    years,
    hierarchyEntries,
    effectiveVisibleIds,
    difficultyFilter,
    valueDisplayMode,
    enabled: viewMode === 'yearly'
  })

  const option = viewMode === 'monthly' ? monthlyOption : yearlyOption

  return (
    <DetailsCard className="p-4 mt-4" data-cy="data-points-chart">
      <H3 className="text-foreground mb-4">{t('comparativeAnalysis.chart.title')}</H3>
      <ChartToolbar
        hierarchyEntries={hierarchyEntries}
        visibleSeriesIds={visibleSeriesIds}
        onVisibleSeriesIdsChange={onVisibleSeriesIdsChange}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={onCategoryFilterChange}
        difficultyFilter={difficultyFilter}
        onDifficultyFilterChange={onDifficultyFilterChange}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        valueDisplayMode={valueDisplayMode}
        onValueDisplayModeChange={onValueDisplayModeChange}
        selectedYear={selectedYear}
        onSelectedYearChange={onSelectedYearChange}
        availableYears={availableYears}
      />
      {isLoading ? (
        <div className="flex items-center justify-center py-9">
          <Loading size="large" />
        </div>
      ) : (
        <EChart
          key={`${effectiveVisibleIds.join(',')}-${valueDisplayMode}`}
          option={option}
          className="w-full"
        />
      )}
    </DetailsCard>
  )
}
